import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Button, Divider, Tag, Tooltip, Typography,
} from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { BusinessPartnerTableRow, useBusinessPartnersGet } from '../../../../../hooks/api/businessPartners';

import styles from './index.module.scss';
import Loading from '../../../../Common/Loading';
import { getBusinessStatusColor } from '../../../BusinessPartners';
import SendWelcomeMailBtn from '../SendWelcomeMailBtn';

const { Title, Text } = Typography;

export function ContainerNewEmail({ newEmail }: { newEmail?: string }) {
  const isPending = !!newEmail;

  return (
    <Tooltip title={isPending ? `Confirmation email sent to: ${newEmail}` : undefined}>
      <Tag
        color={getBusinessStatusColor(isPending ? 'pending' : 'active')}
        className="capitalize-first-letter"
        style={{ fontSize: '10px' }}
      >
        {isPending ? 'pending' : 'active'}
      </Tag>
    </Tooltip>
  );
}

interface BusinessPartnersProps {
  style?: React.CSSProperties;
  triggerRefresh?: string | number;
}

function BusinessPartners({ style, triggerRefresh }: BusinessPartnersProps) {
  const { id } = useParams();
  const businessPartnersLoad = useBusinessPartnersGet();

  const [businessPartnersList, setBusinessPartners] = useState<BusinessPartnerTableRow[]>([]);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    if (id && !businessPartnersLoad.loading) {
      fetchMore(1);
    }
  }, [id]);

  useEffect(() => {
    if (triggerRefresh && !businessPartnersLoad.loading) {
      fetchMore(1, true);
    }
  }, [triggerRefresh]);

  const fetchMore = (page: number, replace = false) => {
    businessPartnersLoad.fetch({ userId: id, pageSize: 50, page })
      .then((res) => {
        if (replace) {
          setCurrentPage(1);
          setBusinessPartners(res?.data || []);
        } else {
          if (res?.meta?.pageCount !== page) {
            setCurrentPage(page);
          }
          if (res?.data) {
            setBusinessPartners((prevState) => (
              [...prevState, ...res.data]
            ));
          }
        }
      });
  };

  // useEffect(() => {
  //   if (businessPartnersLoad.data?.data?.length) {
  //     setBusinessPartners(businessPartnersLoad.data?.data);
  //   }
  // }, [businessPartnersLoad.data]);

  return (
    <div className={styles.container} style={style}>
      <h4>Business partners:</h4>

      {businessPartnersLoad.loading ? <Loading absolute /> : null}

      <div className={styles.scrollContainer}>
        {businessPartnersList?.length ? (
          businessPartnersList.map((item, index) => (
            <div key={item.id} className={styles.partner}>
              <Title level={5} className={styles.partnerNumber} style={{ margin: 0 }}>
                Partner #
                {item.businessPartnerNumber}
                {' - '}
                {`${item.firstName || '-'} ${item.name || '-'}`}
                {' '}
                <ContainerNewEmail newEmail={item.newEmail} />
              </Title>

              <div className={styles.contracts}>
                <Text strong>Contracts:</Text>
                <ul>
                  {item.contracts?.length ? (
                    item.contracts.map((contract) => (
                      <div key={contract.contractNumber} className={styles.contractItem}>
                        <li>
                          <Link to={`/business-partners/${item.id}/edit?activeContract=${contract.contractNumber}`}>
                            {contract.contractNumber}
                          </Link>
                          {contract.tariffId
                            ? <span title={`TariffId ${contract.tariffName}`}>{` (${contract.tariffId})`}</span>
                            : ''}
                          {contract.contractNumber && item?.user?.id ? (
                            <>
                              <Divider type="vertical" />
                              <SendWelcomeMailBtn contractNumber={contract.contractNumber} userId={item?.user?.id} />
                            </>
                          ) : null}
                        </li>
                      </div>
                    ))
                  ) : (
                    <Text type="secondary">No contracts available.</Text>
                  )}
                </ul>
              </div>

              {index < (businessPartnersList?.length || 0) - 1 && <Divider style={{ margin: '8px 0' }} />}
            </div>
          ))
        ) : (
          <Text className={styles.centered} type="secondary">No business partners found.</Text>
        )}
      </div>

      {(businessPartnersLoad.data?.meta.pageCount || 0) > currentPage ? (
        <Button
          onClick={() => fetchMore(currentPage + 1)}
          icon={<ReloadOutlined />}
          loading={businessPartnersLoad.loading}
          style={{ marginTop: '12px' }}
        >
          Load more
        </Button>
      ) : null}
    </div>
  );
}

export default BusinessPartners;
